import PropTypes from "prop-types";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useAlert } from "../../../context/AlertContext";
import { emptyValueObjects } from "../../../utils/ManageArray";
import { FloatingControlComponent } from "../../FormComponent";
import { formatInputDate } from "../../../utils/Time";
import { formatDate, formatSplitKey } from "../../../commons/FormatValue";
import TableForm from "../../TableForm";
import ConfirmModal from "../../ConfirmModal";
import CardDisplayComponent from "../../CardDisplayComponent";

const EditPMNumberStatusForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showAlert } = useAlert();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [data, setData] = useState([editData]);
  const columns = [
    { name: "Pm_No", label: "หมายเลข PM" },
    { name: "Pm_Item", label: "PM/ครั้งที่" },
    { name: "Month_Item", label: "เดือน/ครั้ง" },
    {
      name: "PMPlan.CustContractItem.CustContract.Project_No",
      label: "เลขที่โครงการ"
    },
    { name: "PMPlan.CustContractItem.Contract_No", label: "เลขที่สัญญา" },
    { name: "PMPlan.CustContractItem.Contract_Type", label: "ประเภทสัญญา" },
    {
      name: "PMPlan.CustContractItem.CustContract.Contract_Start_Date",
      label: "วันที่เริ่มสัญญา",
      format: (row) =>
        formatDate(
          row?.PMPlan?.CustContractItem?.CustContract?.Contract_Start_Date
        )
    },
    {
      name: "PMPlan.CustContractItem.CustContract.Contract_End_Date",
      label: "วันที่สิ้นสุดสัญญา",
      format: (row) =>
        formatDate(
          row?.PMPlan?.CustContractItem?.CustContract?.Contract_End_Date
        )
    },
    { name: "PMPlan.Customer.Cus_Code", label: "รหัสลูกค้า/สาขา" },
    { name: "PMPlan.CustContractItem.Item.Item_Code", label: "รหัสสินค้า" },
    {
      name: "PMPlan.CustContractItem.Item.Item_Name_TH",
      label: "รายละเอียดสินค้า"
    },
    {
      name: "Pm_No_Status",
      label: "สถานะ",
      type: "select",
      noneAsync: true,
      options: [
        { value: "OPEN", label: "OPEN" },
        { value: "CANCEL", label: "CANCEL" },
        { value: "CLOSED", label: "CLOSED" }
      ]
    }
  ];
  const inputField = [
    {
      name: "Plan_No",
      label: "หมายเลขแผน PM",
      classNames: { col: "mb-3", label: "mb-1" }
    },
    {
      name: "Plan_Date",
      label: "วันที่สร้างแผน",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      format: (value) => formatInputDate(value, "Local")?.date
    },
    {
      name: "JobType.Job_Type_Name",
      label: "ประเภทงาน",
      classNames: { col: "mb-3", label: "mb-1" }
    },
    {
      name: "createdBy",
      label: "ผู้สร้างแผน",
      classNames: { col: "mb-3", label: "mb-1" }
    },
    { name: "Customer.Cus_Code", label: "รหัสลูกค้า" },
    { name: "Customer.Cus_Name", label: "ชื่อลูกค้า", size: 6 },
    { name: "Plan_Status", label: "สถานะแผน" }
  ];

  const checkValidity = () => {
    if (emptyValueObjects(data.Array, ["Plan_No"]).length > 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(!showModal);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback && editData) onSubmitCallback(dataCallback);
  };

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          header={{
            title: { text: "แก้ไขสถานะหมายเลข PM", className: "text-center" }
          }}
          body={{
            as: Row,
            element: inputField.map(
              ({ name, label, type, size, classNames, format }) => {
                const PMPlan = data?.[0]?.PMPlan;
                const value = formatSplitKey(name, ".", PMPlan);
                let result = (format && format(value)) || value;

                return (
                  <Col key={name} sm={size ?? 3} className={classNames?.col}>
                    <FloatingControlComponent
                      disabled
                      type={type}
                      name={name}
                      label={label}
                      className={classNames}
                      placeholder="โปรดระบุ"
                      value={result}
                    />
                  </Col>
                );
              }
            )
          }}
        />

        <CardDisplayComponent
          header={{ title: { text: "รายละเอียดสินค้า" } }}
          body={{
            element: (
              <TableForm
                editForm
                data={data}
                columns={columns}
                onChangeCallback={(newData) => setData(newData)}
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  บันทึกการแก้ไข
                </Button>
                <ConfirmModal
                  show={showModal}
                  data={{ data }}
                  content="edit"
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-secondary"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate("/PM_work/create", { state })}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

EditPMNumberStatusForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default EditPMNumberStatusForm;
